<script context="module">
    let currentSequenceId = 0;
    let currentElementId = 0;
    let dropTarget;
</script>


<script>
    import {PATH_PREFIX} from '../../../apiUrls.js';

    import {slide} from 'svelte/transition';
    import {onMount} from 'svelte';
    import {flip} from 'svelte/animate';
    import {createEventDispatcher} from 'svelte';
    import emotion from 'emotion/dist/emotion.umd.min.js';
    import {getColumnTemmplate} from '../../DataList/utils.js';
    import ElementListForm from './ElementListForm.svelte';
    import MenuToggle from './MenuToggle.svelte';
    import {selectable} from './actions/selectable.js';
    import {toggleTabHide, setActiveTab} from '../../TabView/TabView.svelte';
    import {get} from '../../../utils.js';
    import InvalidElementIconRenderer from './InvalidElementIconRenderer.svelte';
    import {
        setSelectedWorkSequences, clearSelectedListItems,
        setSelectedElements, selectedListItems, getWorkSequence,
        getLocalElementInstance, saveToLocalStorage, isDragging
    } from './utils.js';
    import {elementTabs} from '../tabStore.js';
    import {
        roles,
        draftElementDetails,
        elementInstance,
        selectedSequence,
        elementDetails,
        loadingElementDetails,
        isActivityVersionHeaderSelected,
    } from '../workspacesStore.js';
    import {getUrlParam} from '../../../utils.js';
    import {sgettext} from '../../../i18n.js';


    export let getElementDetailViewApi = `${PATH_PREFIX}/sw/api/elements_database/element_from_instance/`;
    export let workSequences;
    export let frameWidth;
    export let columns = {};
    export let editable = true;
    export let role = {};
    export let currentAction;

    const expandedWorkSequencesKey = 'expanded-work-sequences';
    const dispatch = createEventDispatcher();
    const {css} = emotion;

    let rowCss = generateRowCss(columns);
    let elementRowCss = generateElementRowCss(columns);
    let expanded = true;
    let currentContext = null;
    let needToGetElementDetails = false;

    $: updateColumns(columns, frameWidth);
    $: allColumnsHidden = Object.values(columns).filter(col => col.canHide && col.show).length === 0;

    function updateColumns(columns, frameWidth) {
        rowCss = generateRowCss(columns, frameWidth);
        elementRowCss = generateElementRowCss(columns, frameWidth);
    }

    function generateRowCss(columns, frameWidth) {
        return css`
            display: grid;
            grid-template-columns: 25px 25px 25px ${getColumnTemmplate(columns)} 20px;
            grid-auto-rows: fit-content(min-content, max-content);
        `;
    }

    function generateElementRowCss(columns, frameWidth) {
        return css`
        display: grid;
        grid-template-columns: 25px 25px 25px ${getColumnTemmplate(columns)} 20px;
        grid-auto-rows: fit-content(min-content, max-content);
    `;
    }

    function toggleExpand(ws) {
        ws.expanded = !ws.expanded;

        workSequences = [...workSequences];
        role.worksequences = workSequences;

        const expanded = workSequences
            .filter(w => w.expanded)
            .map(w => w.id);
        localStorage.setItem(expandedWorkSequencesKey, JSON.stringify(expanded));

        roles.set([...$roles]);
    }

    function clearSelection() {
        selectedListItems.set([]);
    }

    function isSameItemType(item) {
        let isSame = false;

        if ($selectedListItems.length && (($selectedListItems[0].hasOwnProperty('element_instances') &&
            item.hasOwnProperty('element_instances')) || $selectedListItems[0].hasOwnProperty('element_id') &&
            item.hasOwnProperty('element_id'))) {
            isSame = true;
        }
        return isSame;
    }

    function isSameSequenceWithPreviousSelection(item) {
        const previous = $selectedListItems.length ? $selectedListItems[$selectedListItems.length - 1] : null;
        let isSame = false;
        if (previous && parent) {
            isSame = previous.work_sequence_id === item.work_sequence_id ? true : false;
        }

        return isSame;
    }

    function isSameRoleWithPreviousSelection(item) {
        const previous = $selectedListItems.length ? $selectedListItems[$selectedListItems.length - 1] : null;
        let isSame = false;
        if (previous && item) {
            isSame = previous.role_id === item.role_id ? true : false;
        }

        return isSame;
    }

    async function getIssuesCount() {
        const eevId = $elementDetails ? $elementDetails.eevId : 0;

        if (eevId) {
            const issuesResponse = await get(`${PATH_PREFIX}/sw/api/elements_database/element_version/${eevId}/element-issues/`);
            if (issuesResponse.ok) {
                const issues = await issuesResponse.json();
                const activeTab = $elementTabs.find(t => t.key === 'issues');
                const numOpenIssues = issues.filter(x => x.status === 'OPEN').length;
                activeTab.label = `${sgettext('$$IMPROVEMENTS$$')} (${numOpenIssues})`;
                elementTabs.set([...$elementTabs]);
            }
        }
    }

    async function getElementDetails() {
        if ($elementInstance && $elementInstance.id) {
            const url = `${getElementDetailViewApi}${$elementInstance.id}/`;

            const response = await get(url);
            if (response.ok) {
                let updated = await response.json();
                elementDetails.set(updated);
            }
            loadingElementDetails.set(false);

            getIssuesCount();
        }
    }

    async function handleListItemSelection(event, item, itemType) {
        if ($isActivityVersionHeaderSelected) {
            isActivityVersionHeaderSelected.set(false);
        }
        
        if (event.detail.event.button === 0) {
            if (currentAction === 'cut' || currentAction === 'copy') {
                dispatch('resetMenu', {});
            }
            let updated = [...$roles];
            const existing = $selectedListItems.find(e => e.id === item.id);
            const isShiftKey = event.detail.event.shiftKey;
            let selected = [];

            if (event.detail.event.ctrlKey && navigator.platform === 'Win32' ||
                event.detail.event.metaKey && navigator.platform === 'MacIntel' ||
                isShiftKey) {
                //multi select
                if (isSameItemType(item)) {
                    if (!existing) {
                        if (itemType === 'element') {
                            const ws = getWorkSequence($roles, item.role_id, item.work_sequence_id);
                            if (isShiftKey && isSameSequenceWithPreviousSelection(item)) {
                                const lastSelected = $selectedListItems[$selectedListItems.length - 1];
                                let from = 0;
                                let to = 0;

                                if (item.step > lastSelected.step) {
                                    from = lastSelected.step + 1;
                                    to = item.step;
                                } else {
                                    from = item.step;
                                    to = lastSelected.step - 1;
                                }

                                selected = ws.element_instances.filter(e => {
                                    return e.step >= from && e.step <= to;
                                });

                                selected.forEach(e => e.selected = true);
                            } else {
                                const ei = ws.element_instances.find(e => e.id === item.id);
                                ei.selected = true;
                                selected.push(ei);
                            }
                        } else if (itemType === 'sequence') {
                            const role = $roles.find(r => r.id === item.role_id);
                            if (isShiftKey && isSameRoleWithPreviousSelection(item)) {
                                const lastSelected = $selectedListItems[$selectedListItems.length - 1];
                                let from = 0;
                                let to = 0;

                                if (item.step > lastSelected.step) {
                                    from = lastSelected.step + 1;
                                    to = item.step;
                                } else {
                                    from = item.step;
                                    to = lastSelected.step - 1;
                                }
                                if (role) {
                                    selected = role.worksequences.filter(e => {
                                        return e.step >= from && e.step <= to;
                                    });

                                    selected.forEach(e => e.selected = true);
                                }
                            } else {
                                const ws = role.worksequences.find(e => e.id === item.id);
                                ws.selected = true;
                                selected.push(ws);
                            }
                        }
                        selectedListItems.update(n => [...n, ...selected]);
                    } else {
                        // remove from selection
                        selectedListItems.set($selectedListItems.filter(e => e.id !== item.id));
                        let unselected = null;
                        if (itemType === 'element') {
                            const ws = getWorkSequence($roles, item.role_id, item.work_sequence_id);
                            unselected = ws.element_instances.find(e => e.id === item.id);
                        } else if (itemType === 'sequence') {
                            const role = $roles.find(r => r.id === item.role_id);
                            unselected = role.worksequences.find(w => w.id === item.id);
                        }

                        if (unselected) {
                            delete unselected.selected;
                            delete unselected.active;
                        }
                    }
                } else {
                    // only same item types are allowed for multiple selection
                    return;
                }
            } else {
                // single selection
                if (!existing) {
                    if (itemType === 'element') {
                        elementInstance.update(n => ({
                            id: item.id,
                            name: item.name,
                            worksequenceId: item.work_sequence_id,
                            roleId: item.role_id,
                            versions: item.versions,
                            actionId: item.action_id,
                            type: item.type,
                        }));

                        needToGetElementDetails = true;

                        // If current tab is not visible for new element, switch to first tab
                        if ($elementTabs.find(e => e.active && e.hide)) {
                            const defaultTabKey = $elementTabs[0].key;
                            elementTabs.set(setActiveTab(defaultTabKey, $elementTabs));
                            location.hash = defaultTabKey;
                        }
                    }
                    selectedListItems.set([{...item}]);
                    setCurrentSelection(item);
                }
            }

            saveToLocalStorage('selectedListItems', JSON.stringify($selectedListItems));

            updateSelection(updated, item);

            dispatch('selected', {});
        }
    }

    async function confirmListItemSelection(){
        if (needToGetElementDetails) {
            await getElementDetails();
        }
    }

    function updateSelection(updated, item) {
        if ($selectedListItems.length === 1) {
            setCurrentSelection(item);
        }

        updated = clearSelectedListItems(updated);

        if (item.hasOwnProperty('element_instances')) {
            roles.set(setSelectedWorkSequences(updated, $selectedListItems, currentSequenceId));
        } else {
            const result = setSelectedElements(updated, $selectedListItems, currentElementId);
            roles.set(result);
        }
    }

    function onToggleContextMenu(event, entityType, entity) {
        dispatch('showMenu', {
            event,
            entityType,
            entity
        });

        if ($selectedListItems.length <= 1 && currentAction != 'copy' && currentAction != 'cut') {
            selectedListItems.set([entity]);
            updateSelection($roles, entity);
        }
    }

    function setCurrentSelection(item) {
        if (item.hasOwnProperty('element_instances')) {
            currentSequenceId = item.id;
            currentElementId = 0;
        } else if (item.hasOwnProperty('element_id')) {
            currentElementId = item.id;
            currentSequenceId = 0;
        }

    }

    function getTextValue(item, key) {
        if (item.hasOwnProperty(key) && item[key]) {
            return item[key];
        }
        return '';
    }


    function createNewElement(ws, name) {
        isActivityVersionHeaderSelected.set(false);

        //clear element selection
        roles.set(clearSelectedListItems($roles));
        selectedListItems.set([]);
        if (ws) {

            // Set the selected sequence so that Save + New knows which sequence to create the next Element at.
            selectedSequence.set(ws);

            elementInstance.set({
                id: 0,
                name: name,
                worksequenceId: ws.id,
                roleId: ws.role_id,
                versions: [],
                index: ws.element_instances.length,
                actionId: "",
                type: ""
            });

            elementDetails.set(null);
            draftElementDetails.set(null);
            elementTabs.set(setActiveTab('edit', $elementTabs));
            elementTabs.set(toggleTabHide('edit', true, $elementTabs));
            elementTabs.set(toggleTabHide('view', true, $elementTabs));
            elementTabs.set(toggleTabHide('contextual', true, $elementTabs));

            setTimeout(() => {
                jQuery(`#name`).focus();
            }, 100);
        }

    }

    function loadElementInstance() {
        const roleId = getUrlParam('roleId');
        const sequenceId = getUrlParam('sequenceId');
        const elementInstanceId = getUrlParam('elementInstanceId');
        if(roleId && sequenceId && elementInstanceId) {
            const item = getLocalElementInstance($roles, parseInt(roleId), parseInt(sequenceId), parseInt(elementInstanceId));
            const ws = getWorkSequence($roles, parseInt(roleId), parseInt(sequenceId));

            selectedListItems.set([item]);
            updateSelection($roles, item);
            elementInstance.update(n => ({
                id: item.id,
                name: item.name,
                worksequenceId: item.work_sequence_id,
                roleId: item.role_id,
                versions: item.versions,
                actionId: item.action_id,
                type: item.type,
            }));
            getElementDetails();
            ws.expanded = true;
            if (item.status != 'Draft') {
                elementTabs.set(setActiveTab('view', $elementTabs));
            } else {
                elementTabs.set(setActiveTab('edit', $elementTabs));
            }
        }
    }

    onMount(() => {
        loadElementInstance();
        const expanded = JSON.parse(localStorage.getItem(expandedWorkSequencesKey) || '[]');
        workSequences.forEach(ws => {
            if (expanded.includes(ws.id)) {
                ws.expanded = true;
            }
        })
        workSequences = [...workSequences];
    });

</script>

<style>
    .sequence-row, .element-row {
        border-bottom: 1px solid #dee2e6;
        min-width: 0;
    }

    .sequence-row > div, .element-row > div {
        padding: 3px 8px;
    }

    .sequence-row.wrapper > div {
        padding: 0;
    }

    .toggle {
        text-align: center;
        cursor: pointer;
        cursor: pointer;
    }

    :global(.hover-in) {
        background-color: #ffecc9;
    }

    .selected {
        background-color: #ffecc9;
    }

    .active.selected {
        background-color: #fab636;
    }

    .copied {
        border: 1px #454545 dashed;
    }

    .cut {
        opacity: 0.5;
        border: 1px #454545 dashed;
    }

    .shadow {
        opacity: 0.4;
    }

    .cell {
        /* border: 1px solid red; */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .menu-col {
        margin: auto;
    }

    .wrapper .index {
        padding: 3px;
    }
</style>

{#each workSequences as ws, seq_index (ws.id)}

    <div id="{`sequence${ws.id}`}"
         animate:flip={{duration: 300}}
         class="ignore sequence-row wrapper sequence"
         class:draggable={editable}
         data-id="{ws.id}"
         data-type="sequence"
         data-roleId="{ws.role_id}"
         class:shadow={ws.shadow}
         on:contextmenu|preventDefault|stopPropagation={event => onToggleContextMenu(event, 'sequence', ws)}
    >
        <div class="drag-handler sequence-row {rowCss}"
             use:selectable={{item: ws}}
             on:mouseDown|preventDefault|stopPropagation={(event) => handleListItemSelection(event, ws, 'sequence')}
             on:mouseUp|preventDefault|stopPropagation={confirmListItemSelection}
             class:selected={ws.selected}
             class:active={ws.active}
             class:copied={ws.copied}
             class:cut={ws.cut}
             class:droppable={editable}
        >
            <div class="toggle" on:mousedown|stopPropagation|preventDefault={toggleExpand(ws)}>
                <i class="fas" class:fa5-plus={!ws.expanded} class:fa5-minus={ws.expanded}/>
            </div>
            <div style="font-weight: bold;">
                {#if ws.invalid }
                    <span style="padding-right:5px;"><i class="fa fa-exclamation-circle text-danger"/></span>
                {:else}
                    <span style="padding-left:20px;"></span>
                {/if}
            </div>
            <div class="right index"><strong>{seq_index+1}</strong></div>
            {#each Object.keys(columns) as key, i}
                {#if columns[key].show}
                    <div class="{columns[key].class} cell">
                            {#if columns[key].columnRenderer}
                                <svelte:component
                                    this={columns[key].columnRenderer}
                                    options={columns[key]}
                                    col={key}
                                    row={ws}/>
                        {:else}{getTextValue(ws, key)}{/if}
                    </div>
                {/if}
            {/each}
            <div class="menu-col">
                <MenuToggle disabled="{!editable}" action="{event => onToggleContextMenu(event, 'sequence', ws)}"/>
            </div>
        </div>
        {#if ws.expanded}
            <div class="ignore" transition:slide|local={{ duration: 300 }}>
                {#each ws.element_instances as instance (instance.id)}
                    <div id="{`element${instance.id}`}"
                         animate:flip={{duration: 300}}
                         class="element-row {elementRowCss} wrapper element"
                         use:selectable={{item: instance}}
                         on:mouseDown|preventDefault|stopPropagation={(event) => handleListItemSelection(event, instance, 'element')}
                         on:mouseUp|preventDefault|stopPropagation={confirmListItemSelection}
                         class:selected={instance.selected}
                         class:active={instance.active}
                         class:copied={instance.copied}
                         class:cut={instance.cut}
                         class:draggable={editable}
                         class:shadow={instance.shadow}
                         data-id="{instance.id}"
                         data-type="element"
                         on:contextmenu|preventDefault|stopPropagation="{event => onToggleContextMenu(event, 'element', instance)}"
                    >
                        <div></div>
                        <div class=cell>
                            {#if instance.selected}
                                <InvalidElementIconRenderer row={instance}/>
                            {:else}
                                <InvalidElementIconRenderer row={instance} rowClass=''/>
                            {/if}
                        </div>
                        <div class="right index">{instance.step + 1}</div>
                        {#each Object.keys(columns) as key, i}
                            {#if columns[key].show}
                                <div class="{columns[key].class} {columns[key].truncate ? 'cell' : ''}">
                                    {#if columns[key].columnRenderer}
                                        <svelte:component
                                            this={columns[key].columnRenderer}
                                            options={columns[key]}
                                            col={key}
                                            row={instance}/>
                                    {:else}{getTextValue(instance, key)}{/if}
                                </div>
                            {/if}
                        {/each}
                        <div class="menu-col">
                            <MenuToggle disabled="{!editable}"
                                    action="{event => onToggleContextMenu(event, 'element', instance)}"/>
                        </div>
                    </div>
                {/each}
                {#if editable}
                    <ElementListForm item="{ws}" createNew="{createNewElement}"/>
                {/if}
            </div>
        {/if}

    </div>
{/each}




